import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import consumidorFlorestalService from "../../services/consumidorFlorestal";

function ConsumidorFlorestal() {
  const [consumidores, setConsumidores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedConsumidor, setSelectedConsumidor] = useState(null);
  const [formData, setFormData] = useState({
    numero_documento: "",
    data_inicio: "",
    data_fim: "",
    valor: "",
    observacoes: "",
    status: "ATIVO",
  });

  const carregarConsumidores = async () => {
    setLoading(true);
    try {
      const data = await consumidorFlorestalService.listar();
      setConsumidores(data);
    } catch (err) {
      setError(err.detail || "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarConsumidores();
  }, []);

  const handleOpenDialog = (consumidor = null) => {
    if (consumidor) {
      setSelectedConsumidor(consumidor);
      setFormData({
        numero_documento: consumidor.numero_documento,
        data_inicio: consumidor.data_inicio.split("T")[0],
        data_fim: consumidor.data_fim.split("T")[0],
        valor: consumidor.valor,
        observacoes: consumidor.observacoes,
        status: consumidor.status,
      });
    } else {
      setSelectedConsumidor(null);
      setFormData({
        numero_documento: "",
        data_inicio: "",
        data_fim: "",
        valor: "",
        observacoes: "",
        status: "ATIVO",
      });
    }
    setOpenDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (new Date(formData.data_inicio) > new Date(formData.data_fim)) {
        alert("A data de início não pode ser maior que a data de fim.");
        return;
      }

      if (selectedConsumidor) {
        await consumidorFlorestalService.atualizar(
          selectedConsumidor.id,
          formData
        );
      } else {
        await consumidorFlorestalService.criar(formData);
      }
      setOpenDialog(false);
      carregarConsumidores();
    } catch (err) {
      setError(err.detail || "Erro ao salvar dados");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" } }}
          >
            Consumidor Florestal
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Novo Consumo
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead sx={{ bgcolor: "action.hover" }}>
                <TableRow>
                  <TableCell>Nº Documento</TableCell>
                  <TableCell>Data Início</TableCell>
                  <TableCell>Data Final</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Valor (R$)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : consumidores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  consumidores.map((consumidor) => (
                    <TableRow key={consumidor.id}>
                      <TableCell>{consumidor.numero_documento}</TableCell>
                      <TableCell>
                        {new Date(consumidor.data_inicio).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(consumidor.data_fim).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{consumidor.status_display}</TableCell>
                      <TableCell>
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(consumidor.valor)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="consumidor-dialog-title"
      >
        <DialogTitle id="consumidor-dialog-title">
          {selectedConsumidor ? "Editar Consumo" : "Novo Consumo"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Número do Documento"
                fullWidth
                required
                value={formData.numero_documento}
                onChange={(e) =>
                  setFormData({ ...formData, numero_documento: e.target.value })
                }
              />
              <TextField
                label="Data Início"
                type="date"
                fullWidth
                required
                value={formData.data_inicio}
                onChange={(e) =>
                  setFormData({ ...formData, data_inicio: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Data Final"
                type="date"
                fullWidth
                required
                value={formData.data_fim}
                onChange={(e) =>
                  setFormData({ ...formData, data_fim: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Valor (R$)"
                type="number"
                fullWidth
                required
                value={formData.valor}
                onChange={(e) =>
                  setFormData({ ...formData, valor: e.target.value })
                }
              />
              <TextField
                select
                label="Status"
                fullWidth
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <MenuItem value="ATIVO">Ativo</MenuItem>
                <MenuItem value="VENCIDO">Vencido</MenuItem>
                <MenuItem value="CANCELADO">Cancelado</MenuItem>
                <MenuItem value="SUSPENSO">Suspenso</MenuItem>
              </TextField>
              <TextField
                label="Observações"
                multiline
                rows={4}
                fullWidth
                value={formData.observacoes}
                onChange={(e) =>
                  setFormData({ ...formData, observacoes: e.target.value })
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}

export default ConsumidorFlorestal;
